import React from 'react';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  className = '',
  loading = false,
  leftIcon,
  rightIcon,
  disabled,
  ...props
}) => {
  const baseClasses = `
    inline-flex items-center justify-center font-medium rounded-md 
    transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 
    disabled:opacity-50 disabled:cursor-not-allowed
    hover:shadow-md active:transform active:scale-[0.98]
  `;
  
  const variantClasses = {
    primary: `
      bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 
      shadow-sm border border-transparent
    `,
    secondary: `
      bg-neutral-100 text-neutral-700 hover:bg-neutral-200 focus:ring-neutral-500 
      border border-neutral-300 hover:border-neutral-400
    `,
    outline: `
      bg-transparent text-primary-600 border border-primary-600 
      hover:bg-primary-50 focus:ring-primary-500 hover:border-primary-700
    `,
    danger: `
      bg-error-600 text-white hover:bg-error-700 focus:ring-error-500 
      shadow-sm border border-transparent
    `,
    ghost: `
      bg-transparent text-neutral-700 hover:bg-neutral-100 focus:ring-neutral-500 
      border border-transparent
    `
  };
  
  const sizeClasses = {
    sm: 'text-sm px-3 py-2 gap-1.5',
    md: 'text-sm px-4 py-2.5 gap-2',
    lg: 'text-base px-6 py-3 gap-2.5'
  };
  
  const iconSizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };
  
  const isDisabled = disabled || loading;
  
  const classes = `
    ${baseClasses}
    ${variantClasses[variant]}
    ${sizeClasses[size]}
    ${className}
  `.replace(/\s+/g, ' ').trim();
  
  return (
    <button 
      className={classes} 
      disabled={isDisabled}
      aria-disabled={isDisabled}
      {...props}
    >
      {loading ? (
        <Loader2 className={`${iconSizeClasses[size]} animate-spin`} />
      ) : leftIcon ? (
        <span className={iconSizeClasses[size]}>{leftIcon}</span>
      ) : null}
      
      <span>{children}</span>
      
      {!loading && rightIcon && (
        <span className={iconSizeClasses[size]}>{rightIcon}</span>
      )}
    </button>
  );
};

export default Button;